import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { deadlineStatusLabel } from '@shared/lib/statusLabels';
import {
  useCloseDeadlineMutation,
  useReopenDeadlineMutation,
  useTriggerExecutionTermMutation,
} from '@shared/api';
import { ContractAudit } from '../ContractAudit';
import { ContractHandoffBlock } from '../ContractHandoffBlock';
import { EntityPartiesSection } from '../EntityPartiesSection';
import {
  DangerButtonOutline,
  DEADLINE_KIND_LABELS,
  Empty,
  EXEC_TERM_KIND_LABELS,
  RESPONSIBLE_ROLE_LABELS,
  Row,
  Section,
  TRIGGER_EVENT_LABELS,
} from './styles';

const DangerTitle = styled.h4`
  color: ${({ theme }) => theme.colors.status.danger} !important;
`;

const DangerActions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const TermGroupTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 6px;
`;

import type { Claim, Correspondence, Deadline, ExecutionTerm } from '@entities/legal/types';

interface ContractDeadlinesSectionProps {
  contractId: string;
  contractStatus: string;
  claims: Claim[];
  correspondence?: Correspondence[];
  deadlines: Deadline[];
  executionTerms: ExecutionTerm[];
  can_write: boolean;
  onOpenCounterparty: (id: string) => void;
  onOpenAudit: () => void;
  onOpenConfirmDelete: () => void;
}

export function ContractDeadlinesSection({
  contractId,
  contractStatus,
  claims,
  correspondence,
  deadlines,
  executionTerms,
  can_write,
  onOpenCounterparty,
  onOpenAudit,
  onOpenConfirmDelete,
}: ContractDeadlinesSectionProps) {
  const [triggerExecutionTerm, { isLoading: isTriggeringTerm }] = useTriggerExecutionTermMutation();
  const [closeDeadline, { isLoading: isClosing, originalArgs: closingId }] = useCloseDeadlineMutation();
  const [reopenDeadline, { isLoading: isReopening, originalArgs: reopeningId }] =
    useReopenDeadlineMutation();

  return (
    <>
      <EntityPartiesSection
        scope="contract"
        entityId={contractId}
        isAnalyzing={contractStatus === 'under_review'}
        onOpenCounterparty={onOpenCounterparty}
      />

      <Section>
        <h4>Связанные претензии и иски ({claims.length})</h4>
        {claims.length === 0 ? (
          <Empty>Связанных дел нет.</Empty>
        ) : (
          claims.map((c) => (
            <Row key={c.id}>
              <Badge>{c.claim_type === 'claim' ? 'претензия' : 'иск'}</Badge>
              <span style={{ flex: 1 }}>{c.dispute_type ?? '—'}</span>
              <Badge>{c.stage}</Badge>
            </Row>
          ))
        )}
      </Section>

      <Section>
        <h4>Переписка ({correspondence?.length ?? 0})</h4>
        {!correspondence || correspondence.length === 0 ? (
          <Empty>Привязанных писем нет.</Empty>
        ) : (
          correspondence.map((m) => (
            <Row key={m.id}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{m.subject ?? 'без темы'}</strong>
                {m.sender && (
                  <span style={{ color: 'var(--osnova-text-muted, #888)', marginLeft: 8 }}>· {m.sender}</span>
                )}
              </span>
              <span style={{ color: 'var(--osnova-text-muted, #888)' }}>
                {new Date(m.incoming_at).toLocaleDateString('ru-RU')}
              </span>
            </Row>
          ))
        )}
      </Section>

      <Section>
        <h4>Сроки ({deadlines.length})</h4>
        {deadlines.length === 0 ? (
          <Empty>Активных сроков по договору нет.</Empty>
        ) : (
          deadlines.map((d) => (
            <Row key={d.id}>
              <span>{new Date(d.due_at).toLocaleDateString('ru-RU')}</span>
              <span style={{ flex: 1 }}>
                <strong>{DEADLINE_KIND_LABELS[d.kind] ?? d.kind}</strong>
                {d.note && <span style={{ color: 'var(--osnova-text-muted, #888)', marginLeft: 8 }}>· {d.note}</span>}
              </span>
              <Badge>{deadlineStatusLabel(d.status)}</Badge>
              {can_write &&
                (d.status === 'closed' ? (
                  <Button
                    type="button"
                    $variant="ghost"
                    $size="sm"
                    onClick={() => void reopenDeadline(d.id)}
                    disabled={isReopening && reopeningId === d.id}
                    title="Отметили по ошибке — вернуть срок в работу"
                  >
                    {isReopening && reopeningId === d.id ? '…' : 'Вернуть'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    $variant="secondary"
                    $size="sm"
                    onClick={() => void closeDeadline(d.id)}
                    disabled={isClosing && closingId === d.id}
                    title="Срок исполнен — перестанет считаться просроченным"
                  >
                    {isClosing && closingId === d.id ? '…' : 'Выполнено'}
                  </Button>
                ))}
            </Row>
          ))
        )}
      </Section>

      <Section>
        <h4>Ввод в работу</h4>
        <ContractHandoffBlock contractId={contractId} canWrite={can_write} />
      </Section>

      {executionTerms.length > 0 && (
        <Section>
          <h4>Порядок исполнения ({executionTerms.length})</h4>
          {(() => {
            const groups = new Map<string, ExecutionTerm[]>();
            for (const t of executionTerms) {
              const arr = groups.get(t.kind) ?? [];
              arr.push(t);
              groups.set(t.kind, arr);
            }
            const order = [
              'payment_stage',
              'delivery',
              'work_milestone',
              'quality_acceptance',
              'notification_period',
              'other',
            ];
            const sortedKinds = [...groups.keys()].sort((a, b) => order.indexOf(a) - order.indexOf(b));

            return sortedKinds.map((kind) => {
              const terms = groups.get(kind)!;
              return (
                <div key={kind} style={{ marginBottom: 16 }}>
                  <TermGroupTitle>
                    {EXEC_TERM_KIND_LABELS[kind] ?? kind} ({terms.length})
                  </TermGroupTitle>
                  {terms.map((t) => {
                    const trigger = t.trigger_event ? TRIGGER_EVENT_LABELS[t.trigger_event] ?? t.trigger_event : null;
                    const duration = t.duration_days ? `${t.duration_days} дн.` : null;
                    return (
                      <Row key={t.id} style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <span style={{ flex: 1, minWidth: 200 }}>
                          <strong>{t.title}</strong>
                          {(trigger || duration || t.due_date) && (
                            <span style={{ color: 'var(--osnova-text-muted, #888)', marginLeft: 8 }}>
                              · {[t.due_date ? new Date(t.due_date).toLocaleDateString('ru-RU') : null, trigger, duration].filter(Boolean).join(', ')}
                            </span>
                          )}
                          {t.note && (
                            <span style={{ color: 'var(--osnova-text-muted, #888)', marginLeft: 8 }}>· {t.note}</span>
                          )}
                          {t.clause && (
                            <span style={{ color: 'var(--osnova-text-muted, #888)', marginLeft: 8, fontSize: 12 }}>
                              ({t.clause})
                            </span>
                          )}
                        </span>
                        {t.responsible_role && (
                          <Badge>{RESPONSIBLE_ROLE_LABELS[t.responsible_role] ?? t.responsible_role}</Badge>
                        )}
                        {can_write && t.trigger_event && !t.due_date && (t.duration_days ?? 0) > 0 && (
                          <Button
                            type="button"
                            $size="sm"
                            $variant="secondary"
                            disabled={isTriggeringTerm}
                            onClick={() => {
                              void triggerExecutionTerm({ contractId, termId: t.id });
                            }}
                            title="Зафиксировать наступление события-триггера и перевести условный срок в активный реестр сроков"
                          >
                            Наступило → в сроки
                          </Button>
                        )}
                      </Row>
                    );
                  })}
                </div>
              );
            });
          })()}
        </Section>
      )}

      <Section>
        <h4>История действий</h4>
        <ContractAudit contractId={contractId} />
      </Section>

      <Section>
        <DangerTitle>Опасная зона</DangerTitle>
        <Empty style={{ fontStyle: 'normal' }}>
          Удаление договора снимает все его версии и LLM-выводы по рискам. Связанные претензии и иски остаются в
          реестре, но теряют ссылку на договор. Действие необратимо.
        </Empty>
        <DangerActions>
          <Button
            type="button"
            $variant="secondary"
            onClick={onOpenAudit}
            disabled={contractStatus !== 'analyzed' || !can_write}
            title={
              !can_write
                ? 'Только чтение'
                : contractStatus === 'analyzed'
                  ? 'Скормить наш анализ внешнему ИИ и загрузить аудит обратно'
                  : 'Доступно когда договор проанализирован'
            }
          >
            Внешний аудит
          </Button>
          <DangerButtonOutline
            type="button"
            onClick={onOpenConfirmDelete}
            disabled={!can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            Удалить договор
          </DangerButtonOutline>
        </DangerActions>
      </Section>
    </>
  );
}
